package songwritersnotebook.app.utils

import songwritersnotebook.app.data.ChordDefinition
import songwritersnotebook.app.viewmodels.StudioUiState
import songwritersnotebook.app.data.TrackItem
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.joinAll
import kotlinx.coroutines.launch
import kotlinx.coroutines.yield
import kotlin.math.roundToLong
import kotlinx.coroutines.CompletableDeferred
import songwritersnotebook.app.getPlatform
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.sync.withLock
import songwritersnotebook.app.utils.ChordUtils

// Represents an event scheduled on the timeline
private data class ScheduledEvent(
    val assetPath: String,
    val startFrame: Long,
    val endFrame: Long,
    val volume: Float,
    val trackItemId: String 
)

// Data class for recording events
private data class ScheduledRecording(
    val item: TrackItem,
    val startFrame: Long
)

class StudioPlaybackEngine(
    private val uiStateFlow: StateFlow<StudioUiState>,
    private val audioHandler: AudioHandler,
    private val playbackState: PlaybackState,
    private val getChordLibrary: () -> Map<String, ChordDefinition>,
    private val onBeat: ((Int) -> Unit)? = null
) {
    private val engineScope = CoroutineScope(Dispatchers.Default + SupervisorJob())
    private var playbackJob: Job? = null
    private var isPlaying = false
    private var isEngineStarted = false

    private val sampleRate = 44100

    private val activeEventIds = mutableSetOf<String>()
    private val activeEventsMutex = kotlinx.coroutines.sync.Mutex()
    
    init {
        startEngine()
    }

    suspend fun warmUp() {
        if (!isEngineStarted) return
        NativeDecoder.startPlaybackEngine()
        delay(100)
    }

    private fun startEngine() {
        try {
            NativeDecoder.startPlaybackEngine()
            isEngineStarted = true
        } catch (e: Exception) {
            isEngineStarted = false
        }
    }

    fun play(onPreparingChanged: (Boolean) -> Unit = {}) {
        if (isPlaying) return
        startEngine()
        if (!isEngineStarted) return

        isPlaying = true
        playbackJob?.cancel()

        playbackJob = engineScope.launch {
            // 1. Always prepare all assets first (with 10s timeout fail-safe)
            try {
                onPreparingChanged(true)
                kotlinx.coroutines.withTimeout(10000) {
                    prepareAssets()
                }
            } catch (e: Exception) {
                println("DEBUG_ENGINE: Asset preparation timed out or failed: ${e.message}")
            } finally {
                onPreparingChanged(false)
            }
            
            if (!isPlaying) return@launch

            val platformName = getPlatform().name
            if (platformName.contains("Web", ignoreCase = true)) {
                runWebPlayback()
            } else {
                runPlaybackLoop()
            }
        }
    }

    private suspend fun prepareAssets() {
        val currentState = uiStateFlow.value
        val bps = currentState.settings.bpm.toFloat().coerceAtLeast(1f) / 60f
        val allItems = currentState.tracks.flatMap { it.items }
        val currentChordLibrary = getChordLibrary()
        
        val preparationDeferreds = mutableListOf<CompletableDeferred<Unit>>()

        // A. Prepare Recordings
        for (item in allItems) {
            if (item.type == "recording") {
                val deferred = CompletableDeferred<Unit>()
                preparationDeferreds.add(deferred)
                
                val trimStartMillis = ((item.trimStartBeats / bps) * 1000).toInt()
                val durationMillis = ((item.duration / bps) * 1000).toInt()
                val track = currentState.tracks.find { it.items.any { i -> i.id == item.id } }
                val trackVolume = track?.volume ?: 1.0f

                audioHandler.prepareTrackRecording(
                    itemId = item.id,
                    url = item.data.url ?: "",
                    volume = trackVolume,
                    startMillis = trimStartMillis,
                    durationMillis = durationMillis,
                    preparationDeferred = deferred
                )
            }
        }

        // B. Pre-load instrument samples (Crucial for avoiding loop stalls)
        for (item in allItems) {
            when (item.type) {
                "note" -> {
                    item.data.notes?.forEach { note ->
                        val instrument = item.data.instrument ?: "piano"
                        audioHandler.playNote(note, instrument, 0f)
                    }
                }
                "chord" -> {
                    val chordDef = currentChordLibrary[item.data.name]
                    val instrument = item.data.instrument ?: "piano"
                    val song = uiStateFlow.value.song
                    val notes = if (instrument.equals("guitar", ignoreCase = true)) {
                        chordDef?.guitarTab?.let { tab ->
                            if (song != null) {
                                val tabForConversion = tab.map { it.toIntOrNull() ?: if (it == "x") 'x' else 0 }
                                ChordUtils.tabToNotes(tabForConversion, song.guitarTuning, song.guitarCapo)
                            } else emptyList()
                        } ?: emptyList()
                    } else chordDef?.pianoNotes ?: emptyList()
                    
                    notes.forEach { note ->
                        audioHandler.playNote(note, instrument, 0f)
                    }
                }
            }
        }
        delay(300) // Give audio engine a moment to buffer the sample loads

        preparationDeferreds.awaitAll()
    }

    private suspend fun runWebPlayback() {
        val currentState = uiStateFlow.value
        val bpm = currentState.settings.bpm
        val chordLibrary = getChordLibrary()
        
        val buffer = try {
            audioHandler.renderStudioProject(
                currentState.tracks,
                bpm,
                chordLibrary,
                currentState.song
            )
        } catch (e: Exception) {
            null
        }
        
        if (buffer == null || !isPlaying) {
            runPlaybackLoop()
            return
        }

        val startPositionBeats = playbackState.getCurrentBeat()
        val bps = bpm / 60.0
        val startTimeNanos = getTimeNanos()
        val startMillis = (startPositionBeats / bps * 1000.0).toInt()

        audioHandler.playSingleBuffer(buffer, startMillis) {
            isPlaying = false
        }

        while (engineScope.isActive && isPlaying) {
            val loopStartNanos = getTimeNanos()
            val elapsedNanos = loopStartNanos - startTimeNanos
            val elapsedSeconds = elapsedNanos / 1_000_000_000.0
            
            val currentBeat = startPositionBeats + (elapsedSeconds * bps)
            playbackState.setCurrentBeat(currentBeat.toFloat())
            
            delay(30)
        }
        
        audioHandler.stopPlayback()
    }

    private suspend fun runPlaybackLoop() {
        val currentState = uiStateFlow.value
        val initialBpm = currentState.settings.bpm.toFloat().coerceAtLeast(1f)
        val currentSamplesPerBeat = (sampleRate / (initialBpm / 60f))
        val beatsPerBar = currentState.settings.timeSignature.beats.coerceAtLeast(1)

        val allItems = currentState.tracks.flatMap { it.items }
        val currentChordLibrary = getChordLibrary()

        // 1. Pre-load Metronome Samples
        val metronomeHighId = AudioSampleLoader.loadSoundFromCache("metronome/high.wav")
        val metronomeLowId = AudioSampleLoader.loadSoundFromCache("metronome/low.wav")

        val scheduledEvents = allItems.mapNotNull { item ->
            val track = currentState.tracks.find { tr -> tr.items.any { it.id == item.id } } ?: return@mapNotNull null
            if (track.isMuted) return@mapNotNull null
            val anySoloed = currentState.tracks.any { it.isSoloed }
            if(anySoloed && !track.isSoloed) return@mapNotNull null

            val startFrame = (item.beat * currentSamplesPerBeat).roundToLong()
            val endFrame = ((item.beat + item.duration) * currentSamplesPerBeat).roundToLong()
            val volume = track.volume

            when (item.type) {
                "note" -> {
                    item.data.notes?.map { noteName ->
                        val assetPath = "${item.data.instrument}/$noteName.mp3"
                        ScheduledEvent(assetPath, startFrame, endFrame, volume, item.id + noteName)
                    } ?: emptyList()
                }
                "chord" -> {
                    val chordName = item.data.name
                    val instrumentToPlay = item.data.instrument
                    val chordDef = currentChordLibrary[chordName] ?: return@mapNotNull null
                    val song = currentState.song
                    val notesToPlay = if (instrumentToPlay.equals("guitar", ignoreCase = true)) {
                        val tab = chordDef.guitarTab
                        if (tab != null && song != null) {
                            val tabForConversion = tab.map { it.toIntOrNull() ?: if (it == "x") 'x' else 0 }
                            ChordUtils.tabToNotes(tabForConversion, song.guitarTuning, song.guitarCapo)
                        } else emptyList()
                    } else chordDef.pianoNotes ?: emptyList()

                    if (notesToPlay.isEmpty()) return@mapNotNull null

                    val maxStrumDelayMs = 200L
                    val clampedStrumSpeed = track.strumSpeed.coerceIn(0.05f, 1.0f)
                    val noteDelayMs = (maxStrumDelayMs * (1.0f / clampedStrumSpeed - 1.0f) / (notesToPlay.size - 1).coerceAtLeast(1)).toLong()

                    notesToPlay.mapIndexed { index, noteName ->
                        val assetPath = "$instrumentToPlay/$noteName.mp3"
                        val noteDelayFrames = (noteDelayMs * index / 1000.0 * sampleRate).roundToLong()
                        ScheduledEvent(assetPath, startFrame + noteDelayFrames, endFrame, volume, item.id + noteName + index)
                    }
                }
                else -> null
            }
        }.flatten().filterNotNull().sortedBy { it.startFrame }

        val recordingEvents = allItems.mapNotNull { item ->
            val track = currentState.tracks.find { tr -> tr.items.any { it.id == item.id } } ?: return@mapNotNull null
            if (item.type == "recording" && !track.isMuted && (!currentState.tracks.any { it.isSoloed } || track.isSoloed) ) {
                ScheduledRecording(item, (item.beat * currentSamplesPerBeat).roundToLong())
            } else null
        }.sortedBy { it.startFrame }
        
        var nextRecordingEventIndex = 0
        val bps = currentState.settings.bpm.toFloat().coerceAtLeast(1f) / 60f
        activeEventsMutex.withLock { activeEventIds.clear() }
        val startPositionBeats = playbackState.getCurrentBeat()

        recordingEvents.forEach { recEvent ->
            val recItem = recEvent.item
            if (startPositionBeats >= recItem.beat && startPositionBeats < (recItem.beat + recItem.duration)) {
                if (activeEventsMutex.withLock { activeEventIds.add("rec_${recItem.id}") }) {
                    val offsetMillis = ((startPositionBeats - recItem.beat) / bps * 1000).toInt()
                    audioHandler.startPreparedTrack(itemId = recItem.id, offsetMillis = offsetMillis)
                }
            }
        }
        
        var nextEventIndex = 0
        val startTimeNanos = getTimeNanos()
        var currentFramePosition = (startPositionBeats * currentSamplesPerBeat).toLong()
        var lastMetronomeBeat = kotlin.math.floor(startPositionBeats.toDouble()).toLong() - 1

        while (nextEventIndex < scheduledEvents.size && scheduledEvents[nextEventIndex].startFrame < currentFramePosition) nextEventIndex++
        while (nextRecordingEventIndex < recordingEvents.size && recordingEvents[nextRecordingEventIndex].startFrame < currentFramePosition) nextRecordingEventIndex++

        while (engineScope.isActive && isPlaying) { 
            val loopStartNanos = getTimeNanos()
            currentFramePosition = (startPositionBeats * currentSamplesPerBeat).toLong() + ((getTimeNanos() - startTimeNanos) / 1_000_000_000.0 * sampleRate).toLong()
            val currentBeat = (currentFramePosition / currentSamplesPerBeat).toFloat()
            playbackState.setCurrentBeat(currentBeat)

            // METRONOME: Frame-accurate beat calculation
            val roundedBeat = kotlin.math.floor(currentBeat.toDouble()).toLong()
            if (roundedBeat > lastMetronomeBeat) {
                if (uiStateFlow.value.isMetronomeOn) {
                    val isDownbeat = roundedBeat % beatsPerBar == 0L
                    
                    // Emit beat for visualizer
                    onBeat?.invoke(roundedBeat.toInt())

                    if (!uiStateFlow.value.isMetronomeMuted) {
                        val soundId = if (isDownbeat) metronomeHighId else metronomeLowId
                        if (soundId != null) {
                            // One-shot playback (duration = -1) with reduced volume (1f)
                            NativeDecoder.playSoundNative(soundId, 1f, -1)
                        }
                    }
                }
                lastMetronomeBeat = roundedBeat
            }

            while (nextEventIndex < scheduledEvents.size && scheduledEvents[nextEventIndex].startFrame <= currentFramePosition) {
                val event = scheduledEvents[nextEventIndex]
                if (activeEventsMutex.withLock { activeEventIds.add(event.trackItemId) }) {
                    val soundId = AudioSampleLoader.loadSoundFromCache(event.assetPath)
                    if (soundId != null) {
                        val durationFrames = (event.endFrame - event.startFrame).let { if (it > 0) it.toInt() else -1 }
                        NativeDecoder.playSoundNative(soundId, event.volume, durationFrames)
                    }
                    engineScope.launch {
                        delay(((event.endFrame - event.startFrame) * 1000.0 / sampleRate).toLong() + 500) 
                        activeEventsMutex.withLock { activeEventIds.remove(event.trackItemId) }
                    }
                }
                nextEventIndex++
            }

            while (nextRecordingEventIndex < recordingEvents.size && recordingEvents[nextRecordingEventIndex].startFrame <= currentFramePosition) {
                val recEvent = recordingEvents[nextRecordingEventIndex]
                if (activeEventsMutex.withLock { activeEventIds.add("rec_${recEvent.item.id}") }) {
                    audioHandler.startPreparedTrack(itemId = recEvent.item.id)
                }
                nextRecordingEventIndex++
            }

            val sleepNanos = 10_000_000L - (getTimeNanos() - loopStartNanos)
            if (sleepNanos > 0) delay(sleepNanos / 1_000_000) else yield()
        }
        audioHandler.stopAllTrackRecordings()
        activeEventsMutex.withLock { activeEventIds.clear() }
    }

    fun stop() {
        isPlaying = false 
        playbackJob?.cancel() 
        playbackJob = null
        audioHandler.stopAllTrackRecordings() 
        audioHandler.stopPlayback()
        engineScope.launch {
            activeEventsMutex.withLock { activeEventIds.clear() }
        }
    }

    fun release() {
        stop() 
        engineScope.cancel() 
        NativeDecoder.stopPlaybackEngine() 
        isEngineStarted = false
    }

    fun isPlaying(): Boolean = isPlaying
}
